// Constantes e regras sem zod: a tela de entrada e o menu usam só isto, e o zod fica fora do carregamento inicial do web.

// ---------- Perfis e menus ----------

export const PERFIS = ["admin", "gestao", "recepcao", "profissional", "financeiro"] as const;
export type Perfil = (typeof PERFIS)[number];

export const ROTULO_PERFIL: Record<Perfil, string> = {
  admin: "Administrador",
  gestao: "Gestão",
  recepcao: "Recepção",
  profissional: "Profissional",
  financeiro: "Financeiro",
};

/** Chave de cada item do menu (= caminho da tela). A permissão do usuário é a lista destas chaves. */
export const MENUS = [
  "/",
  "/agenda",
  "/conversas",
  "/pacientes",
  "/prontuario",
  "/cobrar",
  "/caixa",
  "/orcamentos",
  "/mensagens-automaticas",
  "/estoque",
  "/entrada-nota",
  "/financeiro",
  "/contas-a-pagar",
  "/contas-a-receber",
  "/notas-fiscais",
  "/relatorios",
  "/cadastros",
  "/usuarios",
  "/acessos",
  "/seguranca",
  "/empresas",
  "/whatsapp",
  "/configuracao",
] as const;
export type Menu = (typeof MENUS)[number];

/** Menus sugeridos ao criar um usuário de cada perfil; o administrador ajusta depois. */
export const MENUS_PADRAO: Record<Perfil, readonly Menu[]> = {
  // Administrador da clínica: permissão completa, inclusive o prontuário.
  admin: MENUS,
  gestao: MENUS.filter((m) => !["/prontuario", "/usuarios", "/acessos", "/seguranca", "/empresas"].includes(m)),
  recepcao: ["/", "/agenda", "/conversas", "/pacientes", "/cobrar", "/caixa", "/orcamentos"],
  profissional: ["/", "/agenda", "/pacientes", "/prontuario"],
  financeiro: ["/", "/caixa", "/financeiro", "/contas-a-pagar", "/contas-a-receber", "/notas-fiscais", "/relatorios"],
};

/** Quem fez, na auditoria: o usuário, ou o master (sozinho ou "como" o usuário que ele estava vendo). */
export function quemFez(usuario: string | null | undefined, operador: string | null | undefined): string | null {
  if (!operador) return usuario ?? null;
  return usuario ? `${operador} (master, como ${usuario})` : `${operador} (master)`;
}

// ---------- Senha ----------

export const SENHA_MINIMO = 10;

// Senhas comuns recusadas (lista curta, sem consulta externa).
const COMUNS = new Set([
  "1234567890", "12345678910", "123456789a", "0123456789", "1q2w3e4r5t", "qwertyuiop", "asdfghjkl1",
  "password12", "password123", "senha12345", "senha123456", "mudar12345", "trocar1234", "admin12345",
  "administrador", "clinica123", "clinica1234", "iloveyou12", "abc1234567", "brasil1234", "qwerty1234",
  "1111111111", "0000000000", "abcdefghij", "princesa12", "flamengo12", "corinthians", "palmeiras1",
]);

export function problemaSenha(senha: string, contexto: string[] = []): string | null {
  if (senha.length < SENHA_MINIMO) return `Use pelo menos ${SENHA_MINIMO} caracteres`;
  if (senha.length > 200) return "Senha longa demais";
  const minuscula = senha.toLowerCase();
  if (COMUNS.has(minuscula) || /^(.)\1+$/.test(senha)) return "Senha muito comum";
  for (const c of contexto) {
    const parte = c.toLowerCase().split("@")[0]!;
    if (parte.length >= 4 && minuscula.includes(parte)) return "Não use seu nome ou e-mail na senha";
  }
  return null;
}

// ---------- Aparência por usuário ----------

/** "sistema" segue o claro/escuro do aparelho. */
export const MODOS_APARENCIA = ["sistema", "claro", "escuro"] as const;
/** Paleta fixa, com versão para o claro e para o escuro testadas em contraste. "clinica" usa a cor cadastrada pela clínica. */
export const CORES_DESTAQUE = ["clinica", "verde", "azul", "petroleo", "ameixa", "rosa", "terracota", "ouro", "grafite"] as const;
export const TONS_FUNDO = ["marfim", "neutro"] as const;
export type ModoAparencia = (typeof MODOS_APARENCIA)[number];
export type CorDestaque = (typeof CORES_DESTAQUE)[number];
export type TomFundo = (typeof TONS_FUNDO)[number];

export const ROTULO_COR: Record<CorDestaque, string> = {
  clinica: "Cor da clínica",
  verde: "Verde",
  azul: "Azul",
  petroleo: "Petróleo",
  ameixa: "Ameixa",
  rosa: "Rosa",
  terracota: "Terracota",
  ouro: "Ouro",
  grafite: "Grafite",
};
